// src/components/coupon/CustomerSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Button, Input, Modal, Pagination, Space, Spin, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getCustomerListApi, type CustomerListItem } from "src/api/customer/customerApi";

export type SelectedCustomer = {
  id: number;
  username: string;
  email: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  selectedItems?: SelectedCustomer[];
  onClose: () => void;
  onConfirm: (selectedItems: SelectedCustomer[]) => void;
};

export default function CustomerSelectModal({
  open,
  selectedIds,
  selectedItems,
  onClose,
  onConfirm,
}: Props) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(10);
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["customer/list/modal", search, page, size] : null,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getCustomerListApi({
        username: search.trim() || undefined,
        page: apiPage,
        size: size,
        order: "createdAt,desc",
      });
    },
    { revalidateOnFocus: false }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const handleConfirm = () => {
    const result: SelectedCustomer[] = tempSelected.map((id) => {
      const customer = list.find((item) => item.id === id);
      if (customer) {
        return { id: customer.id, username: customer.username, email: customer.email };
      }
      const prevSelected = selectedItems?.find((item) => item.id === id);
      if (prevSelected) return prevSelected;
      return { id, username: `고객 ${id}`, email: "" };
    });
    onConfirm(result);
    onClose();
  };

  const columns: ColumnsType<CustomerListItem> = [
    { title: "ID", dataIndex: "id", width: 80 },
    { title: "이름", dataIndex: "username", width: 100 },
    { title: "이메일", dataIndex: "email" },
    { title: "전화번호", dataIndex: "phone", width: 130 },
    {
      title: "상태",
      dataIndex: "status",
      width: 80,
      render: (value: string) => {
        const labels: Record<string, string> = {
          ACTIVE: "활성",
          INACTIVE: "비활성",
          WITHDRAWN: "탈퇴",
        };
        return labels[value] || value;
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="고객 선택"
      width={900}
      centered
      footer={
        <Space>
          <Button onClick={onClose}>취소</Button>
          <Button type="primary" onClick={handleConfirm}>
            선택
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          onPressEnter={() => setPage(1)}
          placeholder="이름 검색"
        />

        <Typography.Text type="secondary">선택된 고객: {tempSelected.length}명</Typography.Text>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={list}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: tempSelected,
              onChange: (selectedRowKeys) => {
                setTempSelected(selectedRowKeys.map((k) => Number(k)));
              },
            }}
            onRow={(record) => ({
              onClick: () => {
                setTempSelected((prev) =>
                  prev.includes(record.id)
                    ? prev.filter((id) => id !== record.id)
                    : [...prev, record.id]
                );
              },
            })}
          />
        )}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            current={page}
            total={Math.max(1, totalElements)}
            pageSize={size}
            onChange={(nextPage) => setPage(nextPage)}
            showSizeChanger={false}
          />
        </div>
      </Space>
    </Modal>
  );
}
